import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  getOtherEmailConnection,
  testOtherEmailImap,
  testOtherEmailSmtp,
  connectOtherEmail,
  disconnectOtherEmail,
  syncOtherEmail,
  getCompanyIntegrations,
  getCompanyIntegrationsHealth,
} from '../api';
import { IntegrationLogo } from '../components/Integrations/IntegrationsGrid';
import { IntegrationAlertBanner } from '../components/Integrations/IntegrationSharedComponents';
import RequestDrawer from '../components/Integrations/RequestDrawer';
import {
  TableSkeleton,
  Switch,
  SideDrawer,
  Button,
  CustomSelect,
  PasswordInput,
} from '@/components';
import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';
import {
  DashboardPage,
  DashboardCard,
  DashboardTableWrapper,
  DashboardTable,
  DashboardTableHeader,
  DashboardTableBody,
  DashboardTableRow,
} from '../components/PremiumDesign';

function getCompanyId() {
  try {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const userObj = JSON.parse(storedUser);
      return userObj?.company_id || localStorage.getItem('company_id');
    }
  } catch {
    // ignore parse error
  }
  return localStorage.getItem('company_id');
}

const IMAP_SECURITY_OPTIONS = [
  { value: 'ssl', label: 'SSL / TLS (Port 993)' },
  { value: 'starttls', label: 'STARTTLS (Port 143)' },
  { value: 'none', label: 'None (Insecure)' },
];

const SMTP_SECURITY_OPTIONS = [
  { value: 'ssl', label: 'SSL / TLS (Port 465)' },
  { value: 'starttls', label: 'STARTTLS (Port 587)' },
  { value: 'none', label: 'None (Insecure)' },
];

// ── Feature / Capability Cards ────────────────────────────────────────────────
const OTHER_EMAIL_FEATURES = [
  {
    key: 'imap_sync',
    title: 'Two-Way Mailbox Sync',
    desc: 'Read, organize, and auto-sync inbound customer emails directly with CRM records.',
    icon: (
      <svg
        className="w-8 h-8 shrink-0 text-blue-600"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect width="20" height="16" x="2" y="4" rx="2" />
        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
      </svg>
    ),
    bg: 'bg-blue-50 border-blue-100',
  },
  {
    key: 'smtp_delivery',
    title: 'Direct SMTP Delivery',
    desc: 'Send outbound emails directly through your custom SMTP server with native SPF & DKIM signatures.',
    icon: (
      <svg
        className="w-8 h-8 shrink-0 text-emerald-600"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <line x1="22" y1="2" x2="11" y2="13" />
        <polygon points="22 2 15 22 11 13 2 9 22 2" />
      </svg>
    ),
    bg: 'bg-emerald-50 border-emerald-100',
  },
  {
    key: 'universal_support',
    title: 'Universal Host Support',
    desc: 'Full compatibility with Hostinger, Zoho Mail, cPanel, Namecheap, Outlook, or private mail hosts.',
    icon: (
      <svg
        className="w-8 h-8 shrink-0 text-indigo-600"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    ),
    bg: 'bg-indigo-50 border-indigo-100',
  },
  {
    key: 'vault_security',
    title: 'AES-256 Vault Encryption',
    desc: 'Server credentials stored with AES-256 encryption at rest. SSRF protection prevents LAN routing.',
    icon: (
      <svg
        className="w-8 h-8 shrink-0 text-amber-600"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    ),
    bg: 'bg-amber-50 border-amber-100',
  },
];

// ── Disconnect Confirmation Modal ─────────────────────────────────────────────
function DisconnectModal({ opened, onClose, onConfirm, isLoading }) {
  useEffect(() => {
    if (!opened) return undefined;
    const onKey = (event) => {
      if (isLoading) return;
      if (event.key === 'Escape') onClose();
      if (event.key === 'Enter') onConfirm();
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [opened, isLoading, onClose, onConfirm]);

  if (!opened) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="other-email-disconnect-title"
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={isLoading ? undefined : onClose}
      />
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 flex flex-col gap-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center shrink-0">
            <svg
              className="w-5 h-5 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
              />
            </svg>
          </div>
          <div>
            <h3
              id="other-email-disconnect-title"
              className="text-[15px] font-semibold text-gray-900 leading-tight"
            >
              Disconnect Email Account
            </h3>
            <p className="text-[13px] text-gray-500 mt-1 leading-relaxed">
              This will revoke IMAP &amp; SMTP credentials and stop message
              synchronization for this company. Existing synchronized messages
              will remain in your database.
            </p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2.5 pt-1 border-t border-gray-100">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-[13px] font-medium text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 text-[13px] font-semibold text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-60 flex items-center gap-2 shadow-sm"
          >
            {isLoading && (
              <svg
                className="w-3.5 h-3.5 animate-spin"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
              >
                <path d="M12 3a9 9 0 0 1 9 9" opacity="0.3" />
                <path d="M12 3a9 9 0 0 1 9 9" />
              </svg>
            )}
            {isLoading ? 'Disconnecting...' : 'Disconnect'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page Component ───────────────────────────────────────────────────────
// Helper to resolve mail server hostnames for known providers & domains
function resolveServerConfig(providerName = '', rawImap = '', rawSmtp = '') {
  return {
    imapHost: rawImap || '',
    smtpHost: rawSmtp || '',
    imapPort: 993,
    smtpPort: 465,
    imapSecurity: 'ssl',
    smtpSecurity: 'ssl',
    provider: providerName || 'Custom',
  };
}

// Helper to safely unwrap account data from array, paginated results, or nested object
function extractAccountData(source) {
  if (!source) return null;
  if (typeof source === 'string') {
    try {
      const parsed = JSON.parse(source);
      return extractAccountData(parsed);
    } catch {
      return null;
    }
  }
  if (Array.isArray(source)) {
    for (const item of source) {
      const extracted = extractAccountData(item);
      if (
        extracted &&
        (extracted.email ||
          extracted.account_email ||
          extracted.email_address ||
          extracted.imap_host ||
          extracted.smtp_host)
      ) {
        return extracted;
      }
    }
    return source.length > 0 ? extractAccountData(source[0]) : null;
  }
  if (typeof source !== 'object') return null;

  for (const key of [
    'results',
    'accounts',
    'data',
    'items',
    'connections',
    'records',
  ]) {
    if (Array.isArray(source[key])) {
      const extracted = extractAccountData(source[key]);
      if (extracted) return extracted;
    }
  }

  for (const key of [
    'data',
    'account',
    'connection',
    'config',
    'credentials',
    'connection_config',
    'settings',
  ]) {
    if (source[key]) {
      const val =
        typeof source[key] === 'string'
          ? (() => {
              try {
                return JSON.parse(source[key]);
              } catch {
                return null;
              }
            })()
          : source[key];
      if (val && typeof val === 'object') {
        const extracted = extractAccountData(val);
        if (
          extracted &&
          (extracted.email ||
            extracted.account_email ||
            extracted.email_address ||
            extracted.imap_host ||
            extracted.smtp_host)
        ) {
          return extracted;
        }
      }
    }
  }

  return source;
}

// Normalizes security values ('ssl', 'tls', 'SSL', 'STARTTLS', booleans) to 'ssl' | 'starttls' | 'none'
function normalizeSecurity(val, fallbackPort) {
  if (val === true || val === 1 || val === '1') return 'ssl';
  if (!val) {
    const p = Number(fallbackPort);
    if (p === 993 || p === 465) return 'ssl';
    if (p === 143 || p === 587) return 'starttls';
    return 'ssl';
  }
  const s = String(val).toLowerCase().trim();
  if (
    s === 'ssl' ||
    s === 'tls' ||
    s === 'ssl/tls' ||
    s === 'ssl_tls' ||
    s === 'true'
  ) {
    return 'ssl';
  }
  if (s === 'starttls' || s === 'start_tls') {
    return 'starttls';
  }
  if (s === 'none' || s === 'plain' || s === 'insecure' || s === 'false') {
    return 'none';
  }
  return 'ssl';
}

export function OtherEmailDetailPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [requestDrawerOpen, setRequestDrawerOpen] = useState(false);
  const [configDrawerOpen, setConfigDrawerOpen] = useState(false);
  const [customCredentials, setCustomCredentials] = useState(false);

  // Form State
  const [form, setForm] = useState({
    email: '',
    password: '',
    provider_name: 'custom',
    imap_host: '',
    imap_port: 993,
    imap_security: 'ssl',
    imap_username: '',
    imap_password: '',
    smtp_host: '',
    smtp_port: 465,
    smtp_security: 'ssl',
    smtp_username: '',
    smtp_password: '',
  });

  // Diagnostic Test States
  const [imapTestResult, setImapTestResult] = useState(null);
  const [isTestingImap, setIsTestingImap] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState(null);
  const [isTestingSmtp, setIsTestingSmtp] = useState(false);

  // 1. Connection status query
  const {
    data: connectionData,
    isLoading: isConnectionLoading,
    refetch: refetchConnection,
  } = useQuery({
    queryKey: ['other-email-connection', companyId],
    queryFn: () => getOtherEmailConnection(companyId),
    enabled: !!companyId,
    retry: false,
  });

  // 2. Health query
  const {
    data: healthListRaw,
    isLoading: isHealthLoading,
    refetch: refetchHealth,
  } = useQuery({
    queryKey: ['company-integrations-health', companyId],
    queryFn: () => getCompanyIntegrationsHealth(companyId),
    enabled: !!companyId,
  });

  // 3. Company integrations list
  const { data: companyIntegrations = [], refetch: refetchIntegrations } =
    useQuery({
      queryKey: ['company-integrations', companyId],
      queryFn: () => getCompanyIntegrations(companyId),
      enabled: !!companyId,
    });

  const isPageLoading = useDelayedSkeleton(
    isConnectionLoading || isHealthLoading,
  );

  const activeIntegration = companyIntegrations?.find(
    (item) =>
      item.integration_slug === 'other_email' ||
      item.integration_slug === 'other-email' ||
      item.slug === 'other_email' ||
      item.slug === 'other-email',
  );
  const companyIntegrationId = activeIntegration?.id;

  const rawHealthItem = Array.isArray(healthListRaw)
    ? healthListRaw.find(
        (h) =>
          h.integration_slug === 'other_email' ||
          h.integration_slug === 'other-email' ||
          h.slug === 'other_email' ||
          h.slug === 'other-email',
      )
    : null;

  // Resolve config from database (connectionData array/object, activeIntegration.config, healthItem)
  const rawDbConfig =
    extractAccountData(connectionData) ||
    extractAccountData(activeIntegration?.config) ||
    extractAccountData(activeIntegration) ||
    extractAccountData(rawHealthItem?.config) ||
    extractAccountData(rawHealthItem) ||
    {};

  // Auto-populate form when existing connection data or database record is retrieved
  useEffect(() => {
    const config =
      extractAccountData(connectionData) ||
      extractAccountData(activeIntegration?.config) ||
      extractAccountData(activeIntegration) ||
      extractAccountData(rawHealthItem?.config) ||
      extractAccountData(rawHealthItem);

    if (config) {
      const email =
        config.email ||
        config.account_email ||
        config.email_address ||
        config.username ||
        rawHealthItem?.connected_account_name ||
        activeIntegration?.config?.email ||
        '';

      const rawImap =
        config.imap_host ||
        config.imap?.host ||
        config.imap_server ||
        config.incoming_host ||
        config.incoming_server ||
        '';

      const rawSmtp =
        config.smtp_host ||
        config.smtp?.host ||
        config.smtp_server ||
        config.outgoing_host ||
        config.outgoing_server ||
        '';

      const rawProvider = config.provider_name || config.provider || '';

      // Auto-resolve servers based on email/provider if hosts not directly saved
      const resolved = resolveServerConfig(rawProvider, rawImap, rawSmtp);

      const imapHost = rawImap || resolved.imapHost;
      const smtpHost = rawSmtp || resolved.smtpHost;
      const provider = resolved.provider;
      const imapPort =
        config.imap_port ||
        config.imap?.port ||
        config.incoming_port ||
        resolved.imapPort ||
        993;
      const smtpPort =
        config.smtp_port ||
        config.smtp?.port ||
        config.outgoing_port ||
        resolved.smtpPort ||
        465;

      const rawImapSec =
        config.imap_security ||
        config.imap?.security ||
        config.incoming_security ||
        config.security ||
        (config.use_ssl || config.imap?.use_ssl
          ? 'ssl'
          : config.use_tls || config.imap?.use_tls
            ? 'starttls'
            : null);

      const rawSmtpSec =
        config.smtp_security ||
        config.smtp?.security ||
        config.outgoing_security ||
        config.security ||
        (config.use_ssl || config.smtp?.use_ssl
          ? 'ssl'
          : config.use_tls || config.smtp?.use_tls
            ? 'starttls'
            : null);

      const imapSecurity = normalizeSecurity(rawImapSec, imapPort);
      const smtpSecurity = normalizeSecurity(rawSmtpSec, smtpPort);

      const imapUser =
        config.imap_username ||
        config.imap?.username ||
        config.username ||
        email ||
        '';
      const smtpUser =
        config.smtp_username || config.smtp?.username || email || '';

      const hasSeparate = Boolean(
        config.smtp_username && config.smtp_username !== imapUser,
      );
      setCustomCredentials(hasSeparate);

      setForm((prev) => ({
        ...prev,
        email: email || prev.email,
        password:
          config.password || config.imap_password || prev.password || '',
        provider_name: provider !== 'custom' ? provider : prev.provider_name,
        imap_host: imapHost || prev.imap_host,
        imap_port: imapPort || prev.imap_port || 993,
        imap_security: imapSecurity || prev.imap_security || 'ssl',
        imap_username: imapUser || prev.imap_username,
        smtp_host: smtpHost || prev.smtp_host,
        smtp_port: smtpPort || prev.smtp_port || 465,
        smtp_security: smtpSecurity || prev.smtp_security || 'ssl',
        smtp_username: smtpUser || prev.smtp_username,
      }));
    }
  }, [connectionData, activeIntegration, rawHealthItem]);

  const handleEmailChange = (e) => {
    const val = e.target.value;
    setForm((prev) => ({
      ...prev,
      email: val,
      imap_username: customCredentials ? prev.imap_username : val,
      smtp_username: customCredentials ? prev.smtp_username : val,
    }));
  };

  const handlePasswordChange = (e) => {
    const val =
      typeof e.target.value === 'string'
        ? e.target.value.replace(/\s+/g, '')
        : e.target.value;
    setForm((prev) => ({
      ...prev,
      password: val,
      imap_password: customCredentials ? prev.imap_password : val,
      smtp_password: customCredentials ? prev.smtp_password : val,
    }));
  };

  const handleChange = (field, value) => {
    let cleanVal = value;
    if (
      field === 'imap_password' ||
      field === 'smtp_password' ||
      field === 'password'
    ) {
      cleanVal = typeof value === 'string' ? value.replace(/\s+/g, '') : value;
    }
    setForm((prev) => {
      const next = { ...prev, [field]: cleanVal };
      if (!customCredentials) {
        if (field === 'email') {
          next.imap_username = cleanVal;
          next.smtp_username = cleanVal;
        }
        if (field === 'password') {
          next.imap_password = cleanVal;
          next.smtp_password = cleanVal;
        }
      }
      return next;
    });
  };

  // Test IMAP Connection
  const handleTestImap = async () => {
    const username =
      (customCredentials ? form.imap_username : null) ||
      form.imap_username ||
      form.email;
    const password =
      (customCredentials ? form.imap_password : null) ||
      form.imap_password ||
      form.password;

    if (!form.imap_host || !username || !password) {
      toast.error('Please enter Email, Password, and IMAP Host to test.');
      return;
    }
    setIsTestingImap(true);
    setImapTestResult(null);
    try {
      const res = await testOtherEmailImap({
        companyId,
        payload: {
          host: form.imap_host,
          port: form.imap_port,
          security: form.imap_security,
          username,
          password,
        },
      });
      const latency =
        res?.latency_ms ??
        res?.latency ??
        res?.data?.latency_ms ??
        res?.data?.latency;
      setImapTestResult({
        ...res,
        latency_ms: latency != null && !isNaN(latency) ? latency : null,
      });
      if (res?.success) {
        toast.success(
          `IMAP Connected!${latency != null && !isNaN(latency) ? ` Latency: ${latency}ms` : ''}`,
        );
      } else {
        toast.error(res?.error || res?.message || 'IMAP Connection failed');
      }
    } catch (err) {
      const errMsg =
        err?.response?.data?.error || err?.message || 'IMAP connection failed';
      setImapTestResult({ success: false, error: errMsg });
      toast.error(errMsg);
    } finally {
      setIsTestingImap(false);
    }
  };

  // Test SMTP Connection
  const handleTestSmtp = async () => {
    const username =
      (customCredentials ? form.smtp_username : null) ||
      form.smtp_username ||
      form.email;
    const password =
      (customCredentials ? form.smtp_password : null) ||
      form.smtp_password ||
      form.password;

    if (!form.smtp_host || !username || !password) {
      toast.error('Please enter Email, Password, and SMTP Host to test.');
      return;
    }
    setIsTestingSmtp(true);
    setSmtpTestResult(null);
    try {
      const res = await testOtherEmailSmtp({
        companyId,
        payload: {
          host: form.smtp_host,
          port: form.smtp_port,
          security: form.smtp_security,
          username,
          password,
        },
      });
      const latency =
        res?.latency_ms ??
        res?.latency ??
        res?.data?.latency_ms ??
        res?.data?.latency;
      setSmtpTestResult({
        ...res,
        latency_ms: latency != null && !isNaN(latency) ? latency : null,
      });
      if (res?.success) {
        toast.success(
          `SMTP Authenticated!${latency != null && !isNaN(latency) ? ` Latency: ${latency}ms` : ''}`,
        );
      } else {
        toast.error(res?.error || res?.message || 'SMTP Connection failed');
      }
    } catch (err) {
      const errMsg =
        err?.response?.data?.error || err?.message || 'SMTP connection failed';
      setSmtpTestResult({ success: false, error: errMsg });
      toast.error(errMsg);
    } finally {
      setIsTestingSmtp(false);
    }
  };

  // Connect mutation
  const { mutate: connectAccount, isPending: isConnecting } = useMutation({
    mutationFn: () => {
      const effectiveImapUser =
        (customCredentials ? form.imap_username : null) ||
        form.imap_username ||
        form.email;
      const effectiveImapPass =
        (customCredentials ? form.imap_password : null) ||
        form.imap_password ||
        form.password;
      const effectiveSmtpUser =
        (customCredentials ? form.smtp_username : null) ||
        form.smtp_username ||
        form.email;
      const effectiveSmtpPass =
        (customCredentials ? form.smtp_password : null) ||
        form.smtp_password ||
        form.password;

      const payload = {
        ...form,
        email_address: form.email,
        email: form.email,
        imap_username: effectiveImapUser,
        imap_password: effectiveImapPass,
        smtp_username: effectiveSmtpUser,
        smtp_password: effectiveSmtpPass,
      };
      return connectOtherEmail({ companyId, payload });
    },
    onSuccess: (res) => {
      toast.success(res?.message || 'Email account connected successfully!');
      setConfigDrawerOpen(false);
      queryClient.invalidateQueries({
        queryKey: ['other-email-connection', companyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['company-integrations', companyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['company-integrations-health', companyId],
      });
      refetchConnection();
      refetchHealth();
      refetchIntegrations();
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err?.message ||
          'Failed to connect email account',
      );
    },
  });

  // Disconnect mutation
  const { mutate: disconnectAccount, isPending: isDisconnecting } = useMutation(
    {
      mutationFn: () => disconnectOtherEmail(companyId),
      onSuccess: (res) => {
        toast.success(res?.message || 'Email account disconnected.');
        setDisconnectOpen(false);
        queryClient.invalidateQueries({
          queryKey: ['other-email-connection', companyId],
        });
        queryClient.invalidateQueries({
          queryKey: ['company-integrations', companyId],
        });
        queryClient.invalidateQueries({
          queryKey: ['company-integrations-health', companyId],
        });
        refetchConnection();
        refetchHealth();
        refetchIntegrations();
      },
      onError: (err) => {
        toast.error(err?.response?.data?.error || 'Failed to disconnect');
      },
    },
  );

  // Manual Sync mutation
  const { mutate: triggerSync, isPending: isSyncing } = useMutation({
    mutationFn: () => syncOtherEmail(companyId),
    onSuccess: (res) => {
      toast.success(res?.message || 'Mailbox sync finished');
      refetchConnection();
      refetchHealth();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.error || 'Sync failed');
    },
  });

  const isConnected = Boolean(
    connectionData?.is_connected ||
    rawDbConfig?.is_connected ||
    rawDbConfig?.is_active ||
    rawDbConfig?.email ||
    rawDbConfig?.imap_host ||
    (Array.isArray(connectionData) && connectionData.length > 0) ||
    activeIntegration?.is_connected ||
    rawHealthItem?.lifecycle_status === 'activated' ||
    (rawHealthItem?.health_status &&
      rawHealthItem?.health_status !== 'disconnected'),
  );

  const activeHealthItem = rawHealthItem || {
    id: companyIntegrationId || null,
    integration_name: 'Business Email (IMAP/SMTP)',
    integration_slug: 'other_email',
    lifecycle_status: isConnected ? 'activated' : 'disconnected',
    lifecycle_status_label: isConnected ? 'Account Activated' : 'Disconnected',
    health_status: isConnected ? 'healthy' : 'disconnected',
    health_status_label: isConnected ? 'Healthy' : 'Disconnected',
    connected_account_name: null,
    failure_reason: null,
  };

  const displayEmail =
    rawDbConfig?.email ||
    rawDbConfig?.account_email ||
    rawDbConfig?.email_address ||
    activeHealthItem?.connected_account_name ||
    activeIntegration?.config?.email ||
    activeIntegration?.config?.account_email ||
    form.email ||
    '';

  const rawDisplayImap =
    rawDbConfig?.imap_host ||
    rawDbConfig?.imap?.host ||
    rawDbConfig?.imap_server ||
    rawDbConfig?.incoming_host ||
    activeIntegration?.config?.imap_host ||
    activeIntegration?.config?.imap?.host ||
    form.imap_host ||
    '';

  const rawDisplaySmtp =
    rawDbConfig?.smtp_host ||
    rawDbConfig?.smtp?.host ||
    rawDbConfig?.smtp_server ||
    rawDbConfig?.outgoing_host ||
    activeIntegration?.config?.smtp_host ||
    activeIntegration?.config?.smtp?.host ||
    form.smtp_host ||
    '';

  const resolvedDisplayServer = resolveServerConfig(
    displayEmail,
    rawDbConfig?.provider_name || rawDbConfig?.provider || form.provider_name,
    rawDisplayImap,
    rawDisplaySmtp,
  );

  const displayProvider =
    resolvedDisplayServer.provider || form.provider_name || 'Custom';

  const resolvedHealthStatus =
    activeHealthItem?.health_status ||
    (isConnected ? 'healthy' : 'disconnected');
  const isHealthy = ['healthy', 'connected', 'activated'].includes(
    String(resolvedHealthStatus).toLowerCase(),
  );

  const appForDrawer = {
    id: 'other_email',
    name: 'Business Email (IMAP/SMTP)',
    logoType: 'other_email',
    description:
      'Connect Gmail, Hostinger, Zoho, cPanel, or any compatible email provider via IMAP and SMTP.',
  };

  return (
    <>
      <DisconnectModal
        opened={disconnectOpen}
        onClose={() => setDisconnectOpen(false)}
        onConfirm={() => disconnectAccount()}
        isLoading={isDisconnecting}
      />

      <DashboardPage>
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/integrations')}
              className="w-8 h-8 rounded-md border border-gray-300 hover:bg-gray-50 text-gray-700 flex items-center justify-center transition-colors focus:outline-none shrink-0 bg-white shadow-sm"
              aria-label="Back to Integrations"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <h1 className="min-w-0 text-lg font-semibold tracking-tight text-gray-900 sm:text-[22px]">
              Business Email (IMAP/SMTP) Details
            </h1>
          </div>
        </div>

        {/* Loading Skeleton */}
        {isPageLoading ? (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start mt-2">
            <div className="xl:col-span-1">
              <DashboardCard className="flex flex-col gap-5 p-5 shadow-sm border border-gray-100 rounded-[16px] bg-white">
                <div className="flex items-center gap-3">
                  <div className="h-[52px] w-[52px] rounded-xl bg-gray-100 animate-pulse" />
                  <div className="flex flex-col gap-2">
                    <div className="h-5 w-36 bg-gray-200 rounded animate-pulse" />
                    <div className="h-4 w-20 bg-gray-100 rounded animate-pulse" />
                  </div>
                </div>
                <div className="flex flex-col gap-3 mt-2">
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className="h-4 w-full bg-gray-100 rounded animate-pulse"
                    />
                  ))}
                </div>
              </DashboardCard>
            </div>
            <div className="xl:col-span-2">
              <TableSkeleton columns={5} rows={4} />
            </div>
          </div>
        ) : (
          <div className="grid min-w-0 grid-cols-1 items-start gap-4 sm:gap-6 xl:grid-cols-3">
            {/* Left Column: Integration Status Card */}
            <div className="xl:col-span-1 flex min-w-0 flex-col gap-4 sm:gap-6">
              <DashboardCard className="flex min-w-0 flex-col gap-5 rounded-[16px] border border-gray-100 bg-white p-4 shadow-sm sm:p-5">
                <div className="flex items-center gap-3">
                  <IntegrationLogo
                    type="other_email"
                    size="h-[52px] w-[52px]"
                  />
                  <div className="flex flex-col items-start gap-1">
                    <h2 className="text-base font-semibold text-gray-900 leading-tight">
                      Business Email (IMAP/SMTP)
                    </h2>
                    <span
                      className={`text-[10px] font-bold px-2.5 py-0.5 rounded uppercase ${
                        isConnected
                          ? 'text-primary bg-primary/10'
                          : 'text-amber-700 bg-amber-50'
                      }`}
                    >
                      {activeHealthItem?.lifecycle_status_label ||
                        (isConnected ? 'Account Activated' : 'Disconnected')}
                    </span>
                  </div>
                </div>

                {/* Unhealthy Alert Banner */}
                {!isHealthy && activeHealthItem?.failure_reason && (
                  <IntegrationAlertBanner
                    type="error"
                    title="Connection Alert"
                    message={activeHealthItem.failure_reason}
                  />
                )}

                {/* Metadata Details */}
                <div className="flex flex-col gap-4 text-sm mt-1">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs text-gray-400 font-medium">
                      Account Email
                    </span>
                    <span className="text-gray-900 text-sm font-semibold break-all">
                      {displayEmail ||
                        (isConnected ? 'Account Connected' : 'Not Connected')}
                    </span>
                  </div>

                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs text-gray-400 font-medium">
                      Email Provider
                    </span>
                    <span className="text-gray-900 text-sm font-medium capitalize">
                      {displayProvider}
                    </span>
                  </div>
                </div>

                {/* Primary Action Buttons */}
                <div className="flex items-center gap-2 pt-3 border-t border-gray-100 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setConfigDrawerOpen(true)}
                    className="bg-primary hover:bg-primary-hover text-white text-[13px] font-semibold py-2 px-4 rounded-md flex items-center gap-2 transition-colors shadow-sm"
                  >
                    <svg
                      className="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    <span>
                      {isConnected ? 'Edit Server Config' : 'Connect Account'}
                    </span>
                  </button>
                  {isConnected && (
                    <>
                      <button
                        onClick={() => triggerSync()}
                        disabled={isSyncing}
                        className="bg-surface-mint hover:bg-primary/20 text-primary border border-primary/30 text-[13px] font-semibold py-2 px-3.5 rounded-md flex items-center gap-2 transition-colors shadow-2xs disabled:opacity-60"
                      >
                        <svg
                          className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                        <span>{isSyncing ? 'Syncing...' : 'Sync Mailbox'}</span>
                      </button>
                      <button
                        onClick={() => setDisconnectOpen(true)}
                        disabled={isDisconnecting}
                        className="bg-red-50 hover:bg-red-100 text-red-600 text-[13px] font-medium py-2 px-3 rounded-md transition-colors"
                      >
                        Disconnect
                      </button>
                    </>
                  )}
                </div>
              </DashboardCard>
            </div>

            {/* Right Column: Server Config & Capabilities */}
            <div className="flex min-w-0 flex-col gap-4 sm:gap-6 xl:col-span-2">
              {/* Integration Status Table */}
              <DashboardTableWrapper className="min-w-0">
                <DashboardTable className="w-full min-w-[640px]">
                  <DashboardTableHeader>
                    <th className="py-3 px-6 text-left font-medium">
                      Integration Name
                    </th>
                    <th className="py-3 px-6 text-left font-medium">
                      Integration Slug
                    </th>
                    <th className="py-3 px-6 text-center font-medium">
                      Lifecycle Status
                    </th>
                    <th className="py-3 px-6 text-center font-medium">
                      Status
                    </th>
                    <th className="py-3 px-6 text-center font-medium w-24">
                      Action
                    </th>
                  </DashboardTableHeader>
                  <DashboardTableBody>
                    <DashboardTableRow className="border-b border-gray-50 last:border-none">
                      <td className="py-4 px-6 text-[13px] text-gray-900 font-medium">
                        {(
                          activeHealthItem.integration_name ||
                          'Business Email (IMAP/SMTP)'
                        ).replace(/Other Email/gi, 'Business Email')}
                      </td>
                      <td className="py-4 px-6 text-[13px] text-gray-900">
                        {activeHealthItem.integration_slug || 'other_email'}
                      </td>
                      <td className="py-4 px-6 text-center">
                        <span
                          className={`inline-block px-3 py-1 text-[11px] font-semibold rounded ${
                            (
                              activeHealthItem.lifecycle_status || ''
                            ).toLowerCase() === 'activated' || isConnected
                              ? 'text-primary bg-primary/10'
                              : 'text-amber-600 bg-amber-50'
                          }`}
                        >
                          {activeHealthItem.lifecycle_status_label ||
                            activeHealthItem.lifecycle_status ||
                            (isConnected
                              ? 'Account Activated'
                              : 'Disconnected')}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <span
                          className={`inline-block px-3 py-1 text-[11px] font-semibold rounded ${
                            isHealthy
                              ? 'text-primary bg-primary/10'
                              : 'text-red-500 bg-red-50'
                          }`}
                        >
                          {activeHealthItem.health_status_label ||
                            activeHealthItem.health_status ||
                            (isHealthy ? 'Healthy' : 'Disconnected')}
                        </span>
                      </td>
                      <td
                        className="py-4 px-6 text-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-center gap-3">
                          <button
                            type="button"
                            onClick={() =>
                              navigate('/integrations/other-email/history')
                            }
                            title="View Health History"
                            className="text-orange-500 hover:opacity-70 transition-opacity"
                            aria-label="View Health History"
                          >
                            <svg
                              className="w-[18px] h-[18px]"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfigDrawerOpen(true)}
                            title="Configure Mail Server"
                            className="text-primary hover:opacity-70 transition-opacity"
                            aria-label="Configure Mail Server"
                          >
                            <svg
                              className="w-[18px] h-[18px]"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                            </svg>
                          </button>
                          <button
                            type="button"
                            disabled={isHealthy}
                            onClick={() => setRequestDrawerOpen(true)}
                            title={
                              isHealthy
                                ? 'Integration is Healthy'
                                : 'View Integration Requests'
                            }
                            className={`transition-opacity ${
                              isHealthy
                                ? 'text-purple-500 opacity-40 cursor-not-allowed'
                                : 'text-purple-500 hover:opacity-70'
                            }`}
                            aria-label="View Integration Requests"
                          >
                            <svg
                              className="w-[18px] h-[18px]"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                              />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </DashboardTableRow>
                  </DashboardTableBody>
                </DashboardTable>
              </DashboardTableWrapper>

              {/* 3. ACTIVE CAPABILITIES / What You'll Unlock Card */}
              <DashboardCard className="flex min-w-0 flex-col gap-4 rounded-[16px] border border-gray-100 bg-white p-4 shadow-sm sm:gap-5 sm:p-5">
                <div className="pb-2 border-b border-gray-100">
                  <h3 className="text-[13px] font-semibold text-gray-700 uppercase tracking-wide">
                    {isConnected ? 'Active Capabilities' : "What You'll Unlock"}
                  </h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {OTHER_EMAIL_FEATURES.map((feature) => (
                    <div
                      key={feature.title}
                      className={`flex gap-3.5 p-4 rounded-xl border ${feature.bg}`}
                    >
                      <div className="shrink-0 mt-0.5">{feature.icon}</div>
                      <div>
                        <p className="text-[13px] font-semibold text-gray-800">
                          {feature.title}
                        </p>
                        <p className="text-[12px] text-gray-500 mt-1 leading-relaxed">
                          {feature.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </DashboardCard>
            </div>
          </div>
        )}
      </DashboardPage>

      {/* Configure Mail Server SideDrawer */}
      <SideDrawer
        isOpen={configDrawerOpen}
        onClose={() => {
          if (!isConnecting) setConfigDrawerOpen(false);
        }}
        width="lg"
        showClose={false}
        bodyClassName="p-0 flex flex-col min-h-0 !overflow-hidden"
        headerClassName="!px-6 !py-5"
        header={
          <div className="flex items-center justify-between w-full gap-3">
            <div>
              <h2 className="text-[20px] leading-[28px] font-semibold text-[#1F2241] font-sf-pro-display">
                Configure Mail Server
              </h2>
              <p className="text-[12px] text-gray-500 mt-0.5 font-normal">
                Connect Gmail, Hostinger, Zoho, cPanel, or any compatible email
                provider via IMAP &amp; SMTP.
              </p>
            </div>
            <button
              type="button"
              onClick={() => !isConnecting && setConfigDrawerOpen(false)}
              className="w-8 h-8 rounded-[8px] bg-primary text-white flex items-center justify-center hover:bg-primary-hover transition-colors focus:outline-none shrink-0"
              disabled={isConnecting}
              aria-label="Close"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.75"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        }
      >
        <form
          id="configure-mail-server-form"
          onSubmit={(e) => {
            e.preventDefault();
            connectAccount();
          }}
          className="flex flex-col h-full overflow-hidden"
          autoComplete="off"
        >
          <div className="flex-1 overflow-y-auto custom-scrollbar p-6 flex flex-col gap-6 bg-white">
            {/* Section 1: Mailbox Credentials */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <h4 className="text-[18px] leading-[26px] font-medium text-[#50AB3D] font-sf-pro-display">
                  Mailbox Credentials
                </h4>
                <span className="text-[12px] text-gray-400 font-medium">
                  Used for both IMAP &amp; SMTP
                </span>
              </div>
              <div className="bg-[#F5F5F5] rounded-[12px] p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-2">
                    <label className="text-[14px] font-medium text-text-secondary">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      placeholder="e.g. sales@yourcompany.com"
                      value={form.email}
                      onChange={handleEmailChange}
                      disabled={isConnecting}
                      className="w-full h-11 px-4 rounded-xl bg-white border border-border-default text-[#1F2241] text-[14px] font-medium placeholder:text-[#868686] focus:outline-none focus:border-primary transition-colors"
                    />
                    <span className="text-[11px] text-gray-400">
                      Your business or mailbox email address.
                    </span>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="text-[14px] font-medium text-text-secondary">
                      Password / App Password{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <PasswordInput
                      size="md"
                      required={!isConnected}
                      placeholder={
                        isConnected ? '••••••••' : 'Enter mailbox password'
                      }
                      value={form.password}
                      onChange={handlePasswordChange}
                      disabled={isConnecting}
                      className="!bg-white"
                    />
                    <span className="text-[11px] text-gray-400">
                      Stored with AES-256 vault encryption at rest.
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2: Incoming Mail Server (IMAP) */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h4 className="text-[18px] leading-[26px] font-medium text-[#50AB3D] font-sf-pro-display">
                  Incoming Mail Server (IMAP)
                </h4>
                <div className="flex items-center gap-2">
                  {imapTestResult && (
                    <span
                      className={`text-[11px] px-2.5 py-0.5 rounded-full border font-medium ${
                        imapTestResult.success
                          ? 'bg-surface-mint text-primary border-primary/30'
                          : 'bg-rose-50 text-rose-700 border-rose-200'
                      }`}
                    >
                      {imapTestResult.success
                        ? `Connected${imapTestResult.latency_ms != null ? ` • ${imapTestResult.latency_ms}ms` : ''}`
                        : 'Connection Failed'}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={handleTestImap}
                    disabled={isTestingImap}
                    className="px-3.5 py-1.5 text-[13px] font-semibold text-primary bg-white hover:bg-surface-mint border border-primary rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50 shadow-2xs"
                  >
                    {isTestingImap && (
                      <svg
                        className="w-3.5 h-3.5 animate-spin text-primary"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                    )}
                    Test IMAP
                  </button>
                </div>
              </div>
              <div className="bg-[#F5F5F5] rounded-[12px] p-4 flex flex-col gap-4">
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
                  <div className="sm:col-span-6 flex flex-col gap-2">
                    <label className="text-[14px] font-medium text-text-secondary">
                      IMAP Host <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="imap.yourdomain.com"
                      value={form.imap_host}
                      onChange={(e) =>
                        handleChange('imap_host', e.target.value)
                      }
                      disabled={isConnecting}
                      className="w-full h-11 px-4 rounded-xl bg-white border border-border-default text-[#1F2241] text-[14px] font-medium font-mono placeholder:text-[#868686] focus:outline-none focus:border-primary transition-colors"
                    />
                  </div>
                  <div className="sm:col-span-2 flex flex-col gap-2">
                    <label className="text-[14px] font-medium text-text-secondary">
                      Port <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      required
                      value={form.imap_port}
                      onChange={(e) =>
                        handleChange('imap_port', parseInt(e.target.value, 10))
                      }
                      disabled={isConnecting}
                      className="w-full h-11 px-4 rounded-xl bg-white border border-border-default text-[#1F2241] text-[14px] font-medium font-mono focus:outline-none focus:border-primary transition-colors"
                    />
                  </div>
                  <div className="sm:col-span-4 flex flex-col gap-2">
                    <label className="text-[14px] font-medium text-text-secondary">
                      Security
                    </label>
                    <CustomSelect
                      options={IMAP_SECURITY_OPTIONS}
                      value={form.imap_security || 'ssl'}
                      onChange={(val) => {
                        handleChange('imap_security', val);
                        if (val === 'ssl') handleChange('imap_port', 993);
                        else if (val === 'starttls' || val === 'none')
                          handleChange('imap_port', 143);
                      }}
                      disabled={isConnecting}
                      className="w-full h-11"
                      textSizeClass="text-[14px] font-medium"
                    />
                  </div>
                </div>

                {/* Advanced IMAP Credentials */}
                {customCredentials && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3 border-t border-gray-200">
                    <div className="flex flex-col gap-2">
                      <label className="text-[13px] font-medium text-text-secondary">
                        IMAP Username (Custom)
                      </label>
                      <input
                        type="text"
                        placeholder={form.email || 'user@domain.com'}
                        value={form.imap_username}
                        onChange={(e) =>
                          handleChange('imap_username', e.target.value)
                        }
                        className="w-full h-11 px-4 rounded-xl bg-white border border-border-default text-[#1F2241] text-[14px] font-medium focus:outline-none focus:border-primary"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[13px] font-medium text-text-secondary">
                        IMAP Password (Custom)
                      </label>
                      <PasswordInput
                        size="md"
                        placeholder={
                          form.password ? 'Using Account Password' : 'Password'
                        }
                        value={form.imap_password}
                        onChange={(e) =>
                          handleChange('imap_password', e.target.value)
                        }
                        disabled={isConnecting}
                        className="!bg-white"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Section 3: Outgoing Mail Server (SMTP) */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h4 className="text-[18px] leading-[26px] font-medium text-[#50AB3D] font-sf-pro-display">
                  Outgoing Mail Server (SMTP)
                </h4>
                <div className="flex items-center gap-2">
                  {smtpTestResult && (
                    <span
                      className={`text-[11px] px-2.5 py-0.5 rounded-full border font-medium ${
                        smtpTestResult.success
                          ? 'bg-surface-mint text-primary border-primary/30'
                          : 'bg-rose-50 text-rose-700 border-rose-200'
                      }`}
                    >
                      {smtpTestResult.success
                        ? `Authenticated${smtpTestResult.latency_ms != null ? ` • ${smtpTestResult.latency_ms}ms` : ''}`
                        : 'Auth Failed'}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={handleTestSmtp}
                    disabled={isTestingSmtp}
                    className="px-3.5 py-1.5 text-[13px] font-semibold text-primary bg-white hover:bg-surface-mint border border-primary rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50 shadow-2xs"
                  >
                    {isTestingSmtp && (
                      <svg
                        className="w-3.5 h-3.5 animate-spin text-primary"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                    )}
                    Test SMTP
                  </button>
                </div>
              </div>
              <div className="bg-[#F5F5F5] rounded-[12px] p-4 flex flex-col gap-4">
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
                  <div className="sm:col-span-6 flex flex-col gap-2">
                    <label className="text-[14px] font-medium text-text-secondary">
                      SMTP Host <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="smtp.yourdomain.com"
                      value={form.smtp_host}
                      onChange={(e) =>
                        handleChange('smtp_host', e.target.value)
                      }
                      disabled={isConnecting}
                      className="w-full h-11 px-4 rounded-xl bg-white border border-border-default text-[#1F2241] text-[14px] font-medium font-mono placeholder:text-[#868686] focus:outline-none focus:border-primary transition-colors"
                    />
                  </div>
                  <div className="sm:col-span-2 flex flex-col gap-2">
                    <label className="text-[14px] font-medium text-text-secondary">
                      Port <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      required
                      value={form.smtp_port}
                      onChange={(e) =>
                        handleChange('smtp_port', parseInt(e.target.value, 10))
                      }
                      disabled={isConnecting}
                      className="w-full h-11 px-4 rounded-xl bg-white border border-border-default text-[#1F2241] text-[14px] font-medium font-mono focus:outline-none focus:border-primary transition-colors"
                    />
                  </div>
                  <div className="sm:col-span-4 flex flex-col gap-2">
                    <label className="text-[14px] font-medium text-text-secondary">
                      Security
                    </label>
                    <CustomSelect
                      options={SMTP_SECURITY_OPTIONS}
                      value={form.smtp_security || 'ssl'}
                      onChange={(val) => {
                        handleChange('smtp_security', val);
                        if (val === 'ssl') handleChange('smtp_port', 465);
                        else if (val === 'starttls')
                          handleChange('smtp_port', 587);
                        else if (val === 'none') handleChange('smtp_port', 25);
                      }}
                      disabled={isConnecting}
                      className="w-full h-11"
                      textSizeClass="text-[14px] font-medium"
                    />
                  </div>
                </div>

                {/* Advanced SMTP Credentials */}
                {customCredentials && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3 border-t border-gray-200">
                    <div className="flex flex-col gap-2">
                      <label className="text-[13px] font-medium text-text-secondary">
                        SMTP Username (Custom)
                      </label>
                      <input
                        type="text"
                        placeholder={form.email || 'user@domain.com'}
                        value={form.smtp_username}
                        onChange={(e) =>
                          handleChange('smtp_username', e.target.value)
                        }
                        className="w-full h-11 px-4 rounded-xl bg-white border border-border-default text-[#1F2241] text-[14px] font-medium focus:outline-none focus:border-primary"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[13px] font-medium text-text-secondary">
                        SMTP Password (Custom)
                      </label>
                      <PasswordInput
                        size="md"
                        placeholder={
                          form.password ? 'Using Account Password' : 'Password'
                        }
                        value={form.smtp_password}
                        onChange={(e) =>
                          handleChange('smtp_password', e.target.value)
                        }
                        disabled={isConnecting}
                        className="!bg-white"
                      />
                    </div>
                  </div>
                )}

                {/* Switch for Advanced */}
                <div className="pt-2 border-t border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <Switch
                      checked={customCredentials}
                      onChange={(val) => {
                        setCustomCredentials(val);
                        if (!val) {
                          setForm((p) => ({
                            ...p,
                            imap_username: p.email,
                            imap_password: p.password,
                            smtp_username: p.email,
                            smtp_password: p.password,
                          }));
                        }
                      }}
                      aria-label="Separate server logins (Advanced)"
                    />
                    <span className="text-[12px] text-gray-600 font-medium select-none">
                      Separate IMAP / SMTP server credentials (Advanced)
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer matching standard SideDrawer button */}
          <div className="flex items-center justify-end px-6 py-5 border-t border-[#EBEBEB] shrink-0 gap-3 bg-white">
            <button
              type="button"
              onClick={() => setConfigDrawerOpen(false)}
              className="h-[44px] px-5 text-[14px] font-semibold text-text-secondary hover:text-text-primary bg-white hover:bg-form-background-light border border-border-default rounded-[8px] transition-colors"
            >
              Cancel
            </button>
            <Button
              type="submit"
              variant="primary"
              disabled={isConnecting}
              className="h-[44px] px-6 text-[15px] font-medium rounded-[8px] gap-2 min-w-[160px] flex items-center justify-center shadow-sm"
            >
              {isConnecting ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  <span>Verifying &amp; Saving...</span>
                </>
              ) : isConnected ? (
                'Update Connection'
              ) : (
                'Connect Account'
              )}
            </Button>
          </div>
        </form>
      </SideDrawer>

      {/* Reconnect Request Drawer */}
      <RequestDrawer
        integration={appForDrawer}
        companyIntegrationId={companyIntegrationId}
        isOpen={requestDrawerOpen}
        onClose={() => setRequestDrawerOpen(false)}
      />
    </>
  );
}

export default OtherEmailDetailPage;
